<template>
  <div class="app-layout" :class="{ 'is-collapsed': collapsed }">
    <!-- Overlay mobile -->
    <div v-if="!collapsed && isMobile" class="mobile-overlay" @click="collapsed = true" />

    <!-- Top bar mobile -->
    <div v-if="isMobile" class="mobile-topbar">
      <button class="toggle-btn" @click="collapsed = !collapsed">
        <PanelLeftOpen v-if="collapsed" :size="16" />
        <PanelLeftClose v-else :size="16" />
      </button>
      <span class="mobile-brand">Tauá Admin</span>
    </div>

    <aside class="sidebar">

      <!-- Topo: logo + toggle -->
      <div class="sidebar-top">
        <div class="brand-row">
          <img src="@/assets/logo.png" alt="Tauá" class="logo" />
          <span class="brand-name">Tauá Admin</span>
        </div>
        <button class="toggle-btn" @click="collapsed = !collapsed" :title="collapsed ? 'Expandir' : 'Recolher'">
          <PanelLeftClose v-if="!collapsed" :size="16" />
          <PanelLeftOpen  v-else            :size="16" />
        </button>
      </div>

      <!-- Indicador de notificações pendentes (ADR-0001) -->
      <NotificationIndicator :user-role="auth.role" :collapsed="collapsed" />

      <!-- Badge de desenvolvedor -->
      <div v-if="auth.user?.is_dev" class="dev-badge">
        <span class="dev-badge-dot" />
        <span class="dev-badge-label">Desenvolvedor</span>
      </div>

      <!-- Card de ambiente Homolog (painel-adm-taua#126) -- só pra quem já vê
           o badge de Desenvolvedor acima; ausência do card já sinaliza produção. -->
      <div v-if="auth.user?.is_dev && isHomolog" class="homolog-badge">
        <span class="homolog-badge-dot" />
        <span class="homolog-badge-label">Homolog</span>
      </div>

      <!-- Nav -->
      <nav class="sidebar-nav">
        <div v-for="group in visibleNavigation" :key="group.section" class="nav-group">
          <button class="nav-section-btn" @click="toggleSection(group.section)" :title="collapsed ? group.section : undefined">
            <span class="nav-section">{{ group.section }}</span>
            <ChevronDown v-if="!collapsed" :size="11" class="section-chevron" :class="{ 'section-chevron--open': isSectionOpen(group.section) }" />
          </button>
          <template v-if="isSectionOpen(group.section)">
          <template v-for="item in group.items" :key="item.label">

            <!-- Item direto (tem rota) -->
            <router-link
              v-if="item.route"
              :to="{ name: item.route }"
              class="nav-item"
              active-class="nav-item--active"
              :title="collapsed ? item.label : undefined"
            >
              <span class="nav-icon-wrap">
                <component :is="item.icon" :size="16" class="nav-icon" />
                <span v-if="item.route === 'mensagens' && dmUnread > 0" class="nav-badge">
                  {{ dmUnread > 99 ? '99+' : dmUnread }}
                </span>
              </span>
              <span class="nav-label">{{ item.label }}</span>
            </router-link>

            <!-- Grupo colapsável (tem filhos) -->
            <template v-else-if="item.children?.length">
              <button
                class="nav-item nav-group-btn"
                :class="{ 'nav-group-btn--open': isGroupOpen(item.label), 'nav-group-btn--childactive': isChildActive(item) }"
                @click="toggleGroup(item.label)"
                :title="collapsed ? item.label : undefined"
              >
                <span class="nav-icon-wrap">
                  <component :is="item.icon" :size="16" class="nav-icon" />
                </span>
                <span class="nav-label">{{ item.label }}</span>
                <ChevronDown :size="13" class="nav-chevron" />
              </button>
              <div v-if="!collapsed && isGroupOpen(item.label)" class="nav-children">
                <router-link
                  v-for="child in item.children"
                  :key="child.route"
                  :to="{ name: child.route }"
                  class="nav-item nav-child-item"
                  active-class="nav-item--active"
                >
                  <span class="nav-icon-wrap">
                    <component :is="child.icon" :size="13" class="nav-icon" />
                  </span>
                  <span class="nav-label">{{ child.label }}</span>
                </router-link>
              </div>
            </template>

          </template>

          </template>
        </div>
      </nav>

      <!-- Rodapé -->
      <div class="sidebar-footer">
        <div class="user-block">
          <div class="user-avatar">{{ userInitials }}</div>
          <div class="user-details">
            <span class="user-name">{{ auth.user?.name }}</span>
            <span class="user-role">{{ auth.user?.hotel ?? roleLabel }} · {{ roleLabel }}</span>
          </div>
        </div>
        <router-link :to="{ name: 'alterar-senha' }" class="logout-btn" title="Alterar senha">
          <KeyRound :size="15" />
        </router-link>
        <button class="logout-btn" @click="handleLogout" title="Sair">
          <LogOut :size="15" />
        </button>
      </div>

    </aside>

    <main class="main-content">
      <router-view />
    </main>

    <ToastNotification />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { LogOut, PanelLeftClose, PanelLeftOpen, ChevronDown, KeyRound } from 'lucide-vue-next'
import ToastNotification from '@/components/ToastNotification.vue'
import { useAuthStore } from '@/stores/auth.store'
import { navigation } from '@/permissions/navigation'
import { filterNavigation } from '@/permissions/access'
import { ROLES } from '@/permissions/roles'
import { registerWebPush, unregisterWebPush, captureWebPushSession } from '@/services/webPushService'
import { getSocket, listDMs, joinDM, disconnectSocket } from '@/services/messagingService'
import NotificationIndicator from '@/components/NotificationIndicator.vue'
import { registerNotificationSource, setNotificationCount } from '@/notifications/registry'
import { useRankPromotionPendingPoll } from '@/notifications/useRankPromotionPendingPoll'
import { useHotelTransferPendingPoll } from '@/notifications/useHotelTransferPendingPoll'
import { useRedemptionPendingPoll } from '@/notifications/useRedemptionPendingPoll'
import { isHomolog } from '@/utils/environment'

// Reaproveita as roles já declaradas na navegação em vez de duplicá-las aqui —
// se um dia "Mérito pendente" ganhar/perder uma role, o indicador acompanha
// sem precisar de outra edição.
function rolesForRoute(routeName) {
  for (const group of navigation) {
    for (const item of group.items) {
      if (item.route === routeName) return item.roles
      const child = item.children?.find(c => c.route === routeName)
      if (child) return child.roles
    }
  }
  return undefined
}

const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Administrador',
  [ROLES.GERENTE]:       'Gerente',
  [ROLES.OPERADOR_BANCO]: 'Op. Banco',
  [ROLES.TAUALEGRE]:     'TauAlegre',
}

export default {
  name: 'AppLayout',

  components: { LogOut, PanelLeftClose, PanelLeftOpen, ChevronDown, KeyRound, ToastNotification, NotificationIndicator },

  setup() {
    const auth = useAuthStore()
    const router = useRouter()
    const route = useRoute()
    const isMobile = ref(window.innerWidth < 768)
    const collapsed = ref(isMobile.value)
    const dmUnread  = ref(0)
    const openGroups = reactive({})
    const openSections = reactive({})

    function onResize() {
      const mobile = window.innerWidth < 768
      if (mobile !== isMobile.value) {
        isMobile.value = mobile
        collapsed.value = mobile
      }
    }

    function toggleGroup(label) {
      openGroups[label] = !openGroups[label]
    }

    function isGroupOpen(label) {
      return !!openGroups[label]
    }

    function isChildActive(item) {
      return item.children?.some(c => c.route === route.name) ?? false
    }

    function toggleSection(section) {
      openSections[section] = !openSections[section]
    }

    function isSectionOpen(section) {
      return openSections[section] !== false // default: open
    }

    // Auto-abre o grupo quando uma rota filha está ativa
    watch(() => route.name, (routeName) => {
      for (const section of navigation) {
        for (const item of section.items) {
          if (item.children?.some(c => c.route === routeName)) {
            openGroups[item.label] = true
          }
        }
      }
    }, { immediate: true })

    async function initDMSocket() {
      if (!auth.user?.id) return
      try {
        const dms = await listDMs()
        dmUnread.value = dms.reduce((s, d) => s + (d.unreadCount ?? 0), 0)
        dms.forEach(d => joinDM(d.conversaId))

        const socket = getSocket()
        socket.on('dm_notify', () => { dmUnread.value++ })
        socket.on('new_dm',    () => {})
      } catch { /* silencioso */ }
    }

    // ── Indicador de notificações pendentes (ADR-0001) ──────────────────────
    registerNotificationSource('mensagens', {
      label: 'Mensagens',
      route: 'mensagens',
      roles: rolesForRoute('mensagens'),
    })
    // dmUnread já é atualizado em tempo real via socket (initDMSocket acima) --
    // só precisa espelhar o valor atual no registro central sempre que mudar.
    watch(dmUnread, (count) => setNotificationCount('mensagens', count), { immediate: true })

    // Pedidos pendentes de transferência de hotel (tickets #131-#134, parent
    // #129) -- mesmo mecanismo de indicador das mensagens acima.
    registerNotificationSource('transferencia-hotel-pendente', {
      label: 'Transferências pendentes',
      route: 'transferencias-hotel-pendentes',
      roles: rolesForRoute('transferencias-hotel-pendentes'),
    })
    useHotelTransferPendingPoll()

    // Aviso de Evolução de Patente (spec #265, ticket #268) -- badge no item
    // "Patentes" da sidebar quando há promoções pendentes.
    registerNotificationSource('patente-promocao-pendente', {
      label: 'Promoções de patente',
      route: 'ranks',
      query: { tab: 'promocoes' },
      roles: rolesForRoute('ranks'),
    })
    useRankPromotionPendingPoll()

    // Resgates de produto aguardando entrega -- badge no item "Produtos"
    // (onde fica a aba "Resgates pendentes").
    registerNotificationSource('resgate-pendente', {
      label: 'Resgates pendentes',
      route: 'produtos',
      roles: rolesForRoute('produtos'),
    })
    useRedemptionPendingPoll()

    // Usa o namespace do plugin (não o window.OneSignal cru) -- seus métodos
    // enfileiram internamente até o SDK real carregar, então é seguro chamar
    // a qualquer momento, sem depender da corrida de quando o script termina
    // de carregar.
    async function getOneSignal() {
      try {
        const { useOneSignal } = await import('@onesignal/onesignal-vue3')
        return useOneSignal()
      } catch {
        return null
      }
    }

    onMounted(() => {
      if (auth.user?.id) {
        getOneSignal().then(os => registerWebPush(os, auth.user.id))
        initDMSocket()
      }
      window.addEventListener('resize', onResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', onResize)
    })

    watch(() => auth.isAuthenticated, (isAuth) => {
      if (isAuth && auth.user?.id) getOneSignal().then(os => registerWebPush(os, auth.user.id))
      else getOneSignal().then(os => unregisterWebPush(os))
    })

    // Tela branca pós-logout (sem F5): `auth.logout()` é uma action `async`
    // mas seu corpo é totalmente síncrono (sem nenhum `await` interno) --
    // ainda assim, `await auth.logout()` força uma volta pela fila de
    // microtasks antes de continuar. Como a mutação de estado do Pinia
    // (`this.user = null`) já dispara o job de flush de renderização do Vue
    // (também via microtask) DURANTE a execução síncrona de `logout()`, esse
    // job de flush fica na fila ANTES da continuação do nosso `await` --
    // ou seja, a página protegida ainda montada (ex: Tema, que só ADMIN
    // acessa) chega a re-renderizar com `auth.user`/`auth.role` já nulos
    // ANTES do router.push começar a trocar de rota. Várias telas leem
    // `auth.hotelId`/`auth.role` reativamente; um erro de render nesse
    // meio-tempo deixa a árvore de componentes quebrada até um F5.
    //
    // Fix: zera o estado do Pinia e dispara a navegação de volta a volta,
    // sem `await` entre as duas chamadas -- fecha a janela de corrida.
    // Efeitos colaterais não-críticos (OneSignal, socket) viram
    // fire-and-forget DEPOIS da navegação, já que não precisam bloquear o
    // redirect.
    function handleLogout() {
      // A sessão é capturada ANTES de auth.logout() zerar o localStorage: o DELETE do token FCM
      // precisa do bearer para se autenticar depois.
      const pushSession = captureWebPushSession()
      auth.logout()
      router.replace({ name: 'login' })
      getOneSignal().then(os => unregisterWebPush(os, pushSession))
      disconnectSocket()
    }

    const isDev = computed(() => auth.user?.is_dev === true)

    const visibleNavigation = computed(() =>
      filterNavigation(navigation, { role: auth.role, isDev: isDev.value })
    )

    const roleLabel = computed(() => ROLE_LABELS[auth.role] ?? auth.role)

    const userInitials = computed(() => {
      const name = auth.user?.name ?? ''
      return name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    })

    return {
      auth, collapsed, isMobile, visibleNavigation, roleLabel, userInitials, isDev,
      handleLogout, dmUnread, isHomolog,
      openGroups, toggleGroup, isGroupOpen, isChildActive,
      openSections, toggleSection, isSectionOpen,
    }
  },
}
</script>

<style scoped>
.app-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

/* ── Sidebar ─────────────────────────────────────────────── */
.sidebar {
  width: 252px;
  min-width: 252px;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  transition: width 0.22s ease, min-width 0.22s ease;
  overflow: hidden;
}

.app-layout.is-collapsed .sidebar {
  width: 64px;
  min-width: 64px;
}

/* ── Badge de desenvolvedor ──────────────────────────────── */
.dev-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 10px 10px 2px;
  padding: 6px 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  flex-shrink: 0;
  overflow: hidden;
  cursor: default;
  user-select: none;
}

.dev-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a34a;
  flex-shrink: 0;
}

.dev-badge-label {
  font-size: 11px;
  font-weight: 600;
  color: #15803d;
  white-space: nowrap;
  overflow: hidden;
}

.app-layout.is-collapsed .dev-badge-label { display: none; }
.app-layout.is-collapsed .dev-badge { justify-content: center; padding: 6px; }

/* ── Card de ambiente Homolog (painel-adm-taua#126) ──────────────────── */
.homolog-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 2px 10px 2px;
  padding: 6px 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  flex-shrink: 0;
  overflow: hidden;
  cursor: default;
  user-select: none;
}

.homolog-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d97706;
  flex-shrink: 0;
}

.homolog-badge-label {
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  white-space: nowrap;
  overflow: hidden;
}

.app-layout.is-collapsed .homolog-badge-label { display: none; }
.app-layout.is-collapsed .homolog-badge { justify-content: center; padding: 6px; }

/* ── Topo ─────────────────────────────────────────────── */
.sidebar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px 12px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
  gap: 8px;
}

.brand-row {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.logo {
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.15s;
  overflow: hidden;
}

.app-layout.is-collapsed .brand-name { opacity: 0; max-width: 0; }

.toggle-btn {
  flex-shrink: 0;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.toggle-btn:hover {
  background: var(--brand-50);
  color: var(--brand-500);
  border-color: var(--brand-200);
}

/* ── Nav ─────────────────────────────────────────────── */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 8px 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-group {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 4px;
}

.nav-section {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  color: #9ca3af;
  padding: 10px 8px 3px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  transition: opacity 0.15s, max-height 0.15s, padding 0.15s;
  max-height: 40px;
}

.app-layout.is-collapsed .nav-section {
  opacity: 0;
  max-height: 0;
  padding: 0;
  pointer-events: none;
}

.nav-section-btn {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; background: none; border: none; cursor: pointer;
  padding: 0; gap: 4px;
}
.section-chevron {
  color: #9ca3af; transition: transform 0.2s; flex-shrink: 0; margin-right: 4px;
}
.section-chevron--open { transform: rotate(180deg); }

.nav-item {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
}

.nav-item:not(.nav-item--active):hover {
  background: var(--brand-50);
  color: var(--brand-500);
}

.nav-item--active {
  background: var(--brand-500);
  color: #fff;
  font-weight: 600;
}

.nav-icon { flex-shrink: 0; }

.nav-icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.nav-badge {
  position: absolute;
  top: -5px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
}

.nav-label {
  opacity: 1;
  transition: opacity 0.15s;
  flex: 1;
  word-break: break-word;
  line-height: 1.35;
  padding-top: 1px;
}

.app-layout.is-collapsed .nav-label {
  opacity: 0;
  max-width: 0;
  flex: none;
  overflow: hidden;
  white-space: nowrap;
}

.app-layout.is-collapsed .nav-item {
  justify-content: center;
  padding: 10px;
}

/* ── Grupo colapsável ────────────────────────────────────── */
.nav-group-btn {
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}

.nav-group-btn--childactive {
  color: var(--brand-500);
}

.nav-group-btn--childactive .nav-icon {
  color: var(--brand-500);
}

.nav-chevron {
  margin-left: auto;
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.2s, opacity 0.15s;
  margin-top: 2px;
}

.nav-group-btn--open .nav-chevron {
  transform: rotate(180deg);
}

.app-layout.is-collapsed .nav-chevron {
  opacity: 0;
  max-width: 0;
  overflow: hidden;
}

.nav-children {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 1px;
  padding-left: 8px;
  border-left: 2px solid #f3f4f6;
  margin-left: 18px;
}

.nav-child-item {
  font-size: 12px;
  padding: 6px 10px;
}

/* ── Rodapé ─────────────────────────────────────────────── */
.sidebar-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  overflow: hidden;
}

.user-block {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #d1fae5;
  color: var(--brand-500);
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.15s;
}

.app-layout.is-collapsed .user-details { opacity: 0; max-width: 0; }

.user-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logout-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  flex-shrink: 0;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}

.logout-btn:hover {
  background: #fee2e2;
  color: #991b1b;
}

/* ── Main ─────────────────────────────────────────────── */
.main-content {
  flex: 1;
  overflow-y: auto;
  background: #f5f5f0;
  padding: 32px;
  min-width: 0;
}

/* ── Mobile top bar ──────────────────────────────────────── */
.mobile-topbar {
  display: none;
}

/* ── Mobile overlay ──────────────────────────────────────── */
.mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 18, 0.45);
  z-index: 199;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 768px) {
  .app-layout {
    flex-direction: column;
    height: 100dvh;
  }

  /* Sidebar vira overlay */
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100dvh;
    z-index: 200;
    width: 252px !important;
    min-width: 252px !important;
    transform: translateX(0);
    transition: transform 0.22s ease;
  }

  .app-layout.is-collapsed .sidebar {
    transform: translateX(-100%);
    width: 252px !important;
    min-width: 252px !important;
  }

  .main-content {
    width: 100%;
    padding: 12px 14px 24px;
    padding-top: 56px; /* espaço para o topbar */
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    gap: 10px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    height: 48px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    padding: 0 14px;
  }

  .mobile-brand {
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
  }
}
</style>
